import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateDashboardLayout from '../components/layouts/CandidateDashboardLayout';
import Icon from '../components/atoms/Icon';
import Badge from '../components/atoms/Badge';
import SessionManager from '../utils/sessionManager';
import { getApplications, getCandidateTrainerSummary } from '../services/api';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    applications: 0,
    activeApplications: 0,
    courses: 0,
    credits: 0,
    recentApplications: [],
    trainerSummary: null, // Added trainer summary
  });
  const [placementAssessments, setPlacementAssessments] = useState([]);
  const [placementLoading, setPlacementLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(null); // null = loading, true/false

  const session = SessionManager.getSession('candidate');
  const candidateId = session?.user_id;
  const candidateName = session?.profile?.full_name || 'User';
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;

  useEffect(() => {
    if (!candidateId) {
      navigate('/login');
      return;
    }
    loadDashboardData();
    loadPlacementAssessments();
    checkVerificationStatus();
  }, [candidateId, navigate]);

  const checkVerificationStatus = async () => {
    try {
      const token = session?.access_token;
      const res = await fetch(`${apiUrl}/verification/status/${candidateId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsVerified(data.status === 'completed' || data.is_verified === true);
      } else {
        setIsVerified(false);
      }
    } catch {
      setIsVerified(false);
    }
  };

  const loadPlacementAssessments = async () => {
    try {
      setPlacementLoading(true);
      const response = await fetch(
        `${apiUrl}/placement-training/assessments/candidate/history`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPlacementAssessments(data.assessments || []);
      }
    } catch (error) {
      console.error('Error loading placement assessments:', error);
    } finally {
      setPlacementLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch applications
      const applications = await getApplications({ candidate_id: candidateId });
      const activeApps = applications.filter(app => 
        ['review', 'interview', 'offer'].includes(app.status)
      );

      // Get enrolled courses from localStorage
      const enrolledCourses = JSON.parse(localStorage.getItem('enrolled_courses') || '[]');
      
      // Get credits from session
      const credits = session?.profile?.credits || 0;

      // Fetch trainer summary
      let trainerSummary = null;
      try {
        trainerSummary = await getCandidateTrainerSummary(candidateId);
      } catch (error) {
        console.log('No trainer data available:', error);
      }

      setStats({
        applications: applications.length,
        activeApplications: activeApps.length,
        courses: enrolledCourses.length,
        credits: credits,
        recentApplications: applications.slice(0, 5),
        trainerSummary: trainerSummary,
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { label: 'Browse Jobs', icon: 'work', path: '/jobs', color: 'blue' },
    { label: 'Placement Training', icon: 'school', path: '/candidate/placement-training', color: 'teal' },
    { label: 'AI Interview Trainer', icon: 'psychology', path: '/ai-trainer', color: 'purple' },
    { label: 'Enhance Resume', icon: 'description', path: '/enhance-resume', color: 'green' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'applied': return 'bg-blue-100 text-blue-700';
      case 'review': return 'bg-yellow-100 text-yellow-700';
      case 'interview': return 'bg-purple-100 text-purple-700';
      case 'offer': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <CandidateDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </CandidateDashboardLayout>
    );
  }

  return (
    <CandidateDashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">Welcome back, {candidateName}!</h1>
            {isVerified === true && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                <Icon name="verified" size={14} /> Verified
              </span>
            )}
            {isVerified === false && (
              <button
                onClick={() => navigate('/verification-welcome')}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold hover:bg-amber-200 transition-colors cursor-pointer"
              >
                <Icon name="gpp_maybe" size={14} /> Not Verified — Click to verify
              </button>
            )}
          </div>
          <p className="text-gray-600 mt-1">Here's what's happening with your account today.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Icon name="work" size={24} className="text-blue-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats.applications}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Total Applications</h3>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Icon name="trending_up" size={24} className="text-green-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats.activeApplications}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Active Applications</h3>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <Icon name="school" size={24} className="text-purple-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats.courses}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Enrolled Courses</h3>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                <Icon name="account_balance_wallet" size={24} className="text-orange-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{stats.credits}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Available Credits</h3>
          </div>

          {/* AI Trainer Stats Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Icon name="psychology" size={24} className="text-indigo-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {stats.trainerSummary?.summary?.total_sessions || 0}
              </span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Training Sessions</h3>
            {stats.trainerSummary?.summary?.average_score && (
              <p className="text-xs text-indigo-600 mt-1">
                Avg Score: {stats.trainerSummary.summary.average_score}%
              </p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActions.map((action) => {
              const colorClasses = {
                blue: 'hover:border-blue-500 hover:bg-blue-50',
                teal: 'hover:border-teal-500 hover:bg-teal-50',
                purple: 'hover:border-purple-500 hover:bg-purple-50', 
                green: 'hover:border-green-500 hover:bg-green-50',
                orange: 'hover:border-orange-500 hover:bg-orange-50'
              };
              
              const iconColorClasses = {
                blue: 'bg-blue-100 text-blue-600',
                teal: 'bg-teal-100 text-teal-600',
                purple: 'bg-purple-100 text-purple-600',
                green: 'bg-green-100 text-green-600', 
                orange: 'bg-orange-100 text-orange-600'
              };
              
              return (
                <button
                  key={action.label}
                  onClick={() => navigate(action.path)}
                  className={`p-4 rounded-lg border-2 border-gray-200 ${colorClasses[action.color]} transition-all group`}
                >
                  <div className={`w-10 h-10 rounded-lg ${iconColorClasses[action.color]} flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                    <Icon name={action.icon} size={20} />
                  </div>
                  <p className="text-sm font-medium text-gray-700 text-center">{action.label}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* AI Trainer Progress */}
        {stats.trainerSummary && stats.trainerSummary.summary.total_sessions > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">AI Interview Training Progress</h2>
              <button
                onClick={() => navigate('/candidate/reports')}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                View All Reports
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-indigo-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600 font-medium">Completed Sessions</p>
                    <p className="text-2xl font-bold text-indigo-900">
                      {stats.trainerSummary.summary.completed_sessions}
                    </p>
                  </div>
                  <Icon name="check_circle" size={24} className="text-indigo-600" />
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Average Score</p>
                    <p className="text-2xl font-bold text-green-900">
                      {stats.trainerSummary.summary.average_score ? `${stats.trainerSummary.summary.average_score}%` : 'N/A'}
                    </p>
                  </div>
                  <Icon name="trending_up" size={24} className="text-green-600" />
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Completion Rate</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {stats.trainerSummary.summary.completion_rate}%
                    </p>
                  </div>
                  <Icon name="pie_chart" size={24} className="text-blue-600" />
                </div>
              </div>
            </div>

            {stats.trainerSummary.summary.recent_session && (
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Recent Training Session</h3>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Icon name="psychology" size={16} className="text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {stats.trainerSummary.summary.recent_session.job_title}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(stats.trainerSummary.summary.recent_session.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    stats.trainerSummary.summary.recent_session.status === 'completed' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {stats.trainerSummary.summary.recent_session.status}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Placement Training Progress */}
        {!placementLoading && placementAssessments.length > 0 && (() => {
          const completedAssessments = placementAssessments.filter(a => a.status === 'completed');
          const passedAssessments = placementAssessments.filter(a => a.passed);
          const inProgressAssessments = placementAssessments.filter(a => a.status === 'in_progress');
          
          // Chart data for assessment status
          const statusChartData = {
            labels: ['Passed', 'Not Passed', 'In Progress'],
            datasets: [{
              data: [
                passedAssessments.length,
                completedAssessments.length - passedAssessments.length,
                inProgressAssessments.length
              ],
              backgroundColor: ['#10b981', '#ef4444', '#3b82f6'],
              borderWidth: 0,
            }]
          };

          // Chart data for scores
          const recentCompleted = completedAssessments.slice(0, 5).reverse();
          const scoresChartData = {
            labels: recentCompleted.map(a => a.position_title.substring(0, 15) + '...'),
            datasets: [{
              label: 'Score %',
              data: recentCompleted.map(a => a.overall_score || 0),
              backgroundColor: recentCompleted.map(a => 
                a.overall_score >= 70 ? '#10b981' : 
                a.overall_score >= 50 ? '#f59e0b' : '#ef4444'
              ),
              borderRadius: 6,
            }]
          };

          const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 13 },
                cornerRadius: 8,
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                max: 100,
                ticks: { callback: (value) => value + '%' }
              }
            }
          };

          const doughnutOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: { padding: 15, font: { size: 12 } }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                cornerRadius: 8,
              }
            },
            cutout: '70%',
          };

          const getGrade = (score) => {
            if (!score) return { grade: 'N/A', color: 'text-gray-400' };
            if (score >= 90) return { grade: 'A+', color: 'text-green-600' };
            if (score >= 80) return { grade: 'A', color: 'text-green-600' };
            if (score >= 70) return { grade: 'B+', color: 'text-blue-600' };
            if (score >= 60) return { grade: 'B', color: 'text-blue-600' };
            if (score >= 50) return { grade: 'C', color: 'text-yellow-600' };
            return { grade: 'F', color: 'text-red-600' };
          };

          return (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Placement Training Progress</h2>
                  <p className="text-sm text-gray-600 mt-1">MNC assessment performance and analytics</p>
                </div>
                <button
                  onClick={() => navigate('/placement-training')}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                >
                  <span>Browse Companies</span>
                  <Icon name="arrow_forward" size={16} />
                </button>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="assessment" size={20} className="text-blue-600" />
                    <span className="text-2xl font-bold text-blue-900">{placementAssessments.length}</span>
                  </div>
                  <p className="text-sm font-medium text-blue-700">Total Assessments</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="check_circle" size={20} className="text-green-600" />
                    <span className="text-2xl font-bold text-green-900">{passedAssessments.length}</span>
                  </div>
                  <p className="text-sm font-medium text-green-700">Passed</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="workspace_premium" size={20} className="text-purple-600" />
                    <span className="text-2xl font-bold text-purple-900">
                      {placementAssessments.filter(a => a.certificate_issued).length}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-purple-700">Certificates</p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center justify-between mb-2">
                    <Icon name="pending" size={20} className="text-orange-600" />
                    <span className="text-2xl font-bold text-orange-900">{inProgressAssessments.length}</span>
                  </div>
                  <p className="text-sm font-medium text-orange-700">In Progress</p>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Status Distribution Chart */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Assessment Status</h3>
                  <div className="h-48 flex items-center justify-center">
                    <Doughnut data={statusChartData} options={doughnutOptions} />
                  </div>
                </div>

                {/* Recent Scores Chart */}
                {recentCompleted.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent Assessment Scores</h3>
                    <div className="h-48">
                      <Bar data={scoresChartData} options={chartOptions} />
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Assessments List */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Assessments</h3>
                <div className="space-y-2">
                  {placementAssessments.slice(0, 3).map((assessment) => {
                    const grade = getGrade(assessment.overall_score);
                    return (
                      <div
                        key={assessment.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/placement-training/assessment/${assessment.id}/results`)}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                            <Icon name="business" size={20} className="text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{assessment.position_title}</p>
                            <p className="text-xs text-gray-600">{assessment.company_name}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {assessment.status === 'completed' ? (
                            <>
                              <div className="text-right">
                                <div className={`text-lg font-bold ${
                                  assessment.overall_score >= 70 ? 'text-green-600' :
                                  assessment.overall_score >= 50 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  {assessment.overall_score?.toFixed(0)}%
                                </div>
                                <div className={`text-xs font-medium ${grade.color}`}>
                                  {grade.grade}
                                </div>
                              </div>
                              {assessment.passed && (
                                <Icon name="verified" size={20} className="text-green-600" />
                              )}
                            </>
                          ) : (
                            <Badge variant="info">In Progress</Badge>
                          )}
                          <Icon name="chevron_right" size={20} className="text-gray-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Recent Applications */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Applications</h2>
            <button
              onClick={() => navigate('/candidate/applications')}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              View All
            </button>
          </div>
          
          {stats.recentApplications.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="work_outline" size={48} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">No applications yet</p>
              <button
                onClick={() => navigate('/jobs')}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Browse Jobs
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.recentApplications.map((app) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate('/candidate/applications')}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Icon name="business" size={24} className="text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{app.job_title}</h3>
                      <p className="text-sm text-gray-600">{app.company}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                      {app.status}
                    </span>
                    <Icon name="chevron_right" size={20} className="text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </CandidateDashboardLayout>
  );
};

export default CandidateDashboard;
